import { useMemo, useRef, useState, useEffect } from "react";

/**
 * Compact multi-track audio timeline showing:
 *   - Clip audio (pink, spans full duration)
 *   - Music (blue, with drag-to-adjust start offset)
 *   - Voiceover (emerald, with drag-to-adjust start offset)
 *   - Sound FX (amber, one bar per slot, drag to reposition)
 *
 * Read-only for clip audio. Draggable handles update parent state.
 * Sits underneath the clip strip in the Studio.
 */
export default function AudioLanes({
  totalDurationSec,
  hasMusic, musicStart, musicVolume, musicName,
  hasVoiceover, voiceoverStart, voiceoverVolume, voiceoverName,
  fxSlots = [],
  hasClipAudio = true, clipMuted = false,
  onChangeMusicStart, onChangeVoiceoverStart, onChangeFxAt,
}) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(null); // { kind: 'music'|'voice'|'fx', fxId }
  const totalDur = Math.max(0.5, totalDurationSec || 1);

  const pxPerSec = 100; // used only for label logic
  const rulerTicks = useMemo(() => {
    const step = totalDur > 30 ? 5 : totalDur > 12 ? 2 : 1;
    const ticks = [];
    for (let s = 0; s <= totalDur; s += step) ticks.push(s);
    if (ticks[ticks.length - 1] < totalDur) ticks.push(Math.floor(totalDur));
    return ticks;
  }, [totalDur]);

  const timeFromEvent = (e) => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0]?.clientX) || 0) - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    return pct * totalDur;
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const t = Math.max(0, Math.min(totalDur, timeFromEvent(e)));
      if (dragging.kind === "voice") onChangeVoiceoverStart?.(Number(t.toFixed(2)));
      else if (dragging.kind === "fx") onChangeFxAt?.(dragging.fxId, Number(t.toFixed(2)));
    };
    const onUp = () => setDragging(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, totalDur, onChangeVoiceoverStart, onChangeFxAt]);

  const startPct = (v) => `${Math.max(0, Math.min(100, (v / totalDur) * 100))}%`;

  const laneClass = "relative h-5 rounded-md bg-white/[0.03] border border-white/5 overflow-visible";

  return (
    <div className="w-full px-3 py-2 bg-[#08080B] border-t border-white/5 space-y-1.5" data-testid="editor-audio-lanes">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-zinc-500">
        <span>Audio timeline</span>
        <span className="tabular-nums">{totalDur.toFixed(1)}s</span>
      </div>

      {/* Time ruler */}
      <div className="relative h-3 border-b border-white/5" ref={trackRef}>
        {rulerTicks.map((s) => (
          <div key={s} className="absolute top-0 h-full text-[8px] text-zinc-600 tabular-nums" style={{ left: startPct(s) }}>
            <div className="w-px h-2 bg-white/10" />
            <div className="-translate-x-1/2 -translate-y-0.5">{s}s</div>
          </div>
        ))}
      </div>

      {/* Clip lane */}
      <div className={laneClass}>
        <div
          className={`absolute top-0 bottom-0 left-0 right-0 rounded-md ${clipMuted ? "bg-red-500/30" : "bg-nexus-pink/40"}`}
          title={clipMuted ? "Clip audio muted" : "Original clip audio"}
        />
        <div className="absolute inset-0 flex items-center px-2">
          <span className="text-[9px] uppercase tracking-widest text-white/90 font-semibold">
            {hasClipAudio ? (clipMuted ? "Clip · muted" : "Clip") : "No clip audio"}
          </span>
        </div>
      </div>

      {/* Music lane */}
      <div className={laneClass}>
        {hasMusic && (
          <div
            className="absolute top-0 bottom-0 rounded-md bg-nexus-blue/50 border border-nexus-blue/60"
            style={{ left: startPct(0), width: `${Math.min(100, ((totalDur - 0) / totalDur) * 100)}%` }}
          >
            <div className="absolute inset-0 flex items-center px-2">
              <span className="text-[9px] uppercase tracking-widest text-white/90 font-semibold truncate">
                Music · {(musicName || "").slice(0, 24)} · {Math.round((musicVolume || 1) * 100)}%
              </span>
            </div>
          </div>
        )}
        {!hasMusic && (
          <div className="absolute inset-0 flex items-center px-2 text-[9px] uppercase tracking-widest text-zinc-600">No music</div>
        )}
      </div>

      {/* Voiceover lane */}
      <div className={laneClass}>
        {hasVoiceover ? (
          <div
            className="absolute top-0 bottom-0 rounded-md bg-emerald-500/50 border border-emerald-500/60 cursor-grab active:cursor-grabbing"
            style={{ left: startPct(voiceoverStart || 0), width: `${Math.min(100 - ((voiceoverStart || 0) / totalDur) * 100, 100)}%` }}
            onPointerDown={(e) => { e.preventDefault(); setDragging({ kind: "voice" }); }}
            data-testid="audio-lanes-voiceover-bar"
            title="Drag to change voiceover start"
          >
            <div className="absolute inset-0 flex items-center px-2">
              <span className="text-[9px] uppercase tracking-widest text-white/95 font-semibold truncate">
                Voice · {(voiceoverName || "").slice(0, 20)} · {Math.round((voiceoverVolume || 1) * 100)}%
              </span>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center px-2 text-[9px] uppercase tracking-widest text-zinc-600">No voiceover</div>
        )}
      </div>

      {/* FX lane */}
      <div className={laneClass}>
        {fxSlots.length === 0 && (
          <div className="absolute inset-0 flex items-center px-2 text-[9px] uppercase tracking-widest text-zinc-600">No sound FX</div>
        )}
        {fxSlots.map((s) => {
          const durSec = Math.max(0.15, (s.durationMs || 500) / 1000);
          const widthPct = Math.max(2.5, (durSec / totalDur) * 100);
          return (
            <div
              key={s.id}
              className="absolute top-0 bottom-0 rounded bg-amber-500/70 border border-amber-500/80 cursor-grab active:cursor-grabbing"
              style={{ left: startPct(s.atSec), width: `${widthPct}%` }}
              onPointerDown={(e) => { e.preventDefault(); setDragging({ kind: "fx", fxId: s.id }); }}
              data-testid={`audio-lanes-fx-${s.id}`}
              title={`${s.title} @ ${s.atSec.toFixed(1)}s`}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[8px] uppercase tracking-widest text-black font-black truncate px-1">{s.title}</span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[9px] text-zinc-600 uppercase tracking-widest pt-0.5">Drag bars to reposition · voiceover + FX only</p>
    </div>
  );
}
